import { BALANCE } from '../balance';
import type { RewardInput } from '../schemas/progression';
import type { StudioShootDefInput } from '../schemas/studio';

/**
 * Studio NANO bookings (studio-nano.md §4.2). Each brief only asks for things the player can buy by the time
 * the studio opens (80⭐: every catalog tier is open) or surely owns (story keepsakes). Later briefs wait for
 * Tiếng lành levels so the pool grows with the studio.
 */
const studioLevel = (level: number) => ({ type: 'studioLevelAtLeast', level }) as const;

export const STUDIO_SHOOTS = [
  // ---- Anh Bảo ----
  {
    // The first booking of all (chapter 5: "ảnh hồ sơ trông chuyên nghiệp mà vẫn dễ thương").
    id: 'shoot_bao_profile',
    npcId: 'npc_bao',
    briefKey: 'studio.shoot.shoot_bao_profile.brief',
    isFirst: true,
    // The free softbox already covers the first two: the lesson is "bring a chair from home".
    criteria: [
      { type: 'theme', tag: 'office', count: 1 },
      { type: 'tag', tag: 'light', count: 1 },
      { type: 'tag', tag: 'seat', count: 1 },
    ],
  },
  {
    id: 'shoot_bao_resign',
    npcId: 'npc_bao',
    briefKey: 'studio.shoot.shoot_bao_resign.brief',
    criteria: [
      { type: 'tag', tag: 'coffee', count: 1 },
      { type: 'tag', tag: 'seat', count: 1 },
      { type: 'theme', tag: 'office', count: 2 },
    ],
  },
  {
    id: 'shoot_bao_weekend',
    npcId: 'npc_bao',
    briefKey: 'studio.shoot.shoot_bao_weekend.brief',
    criteria: [
      { type: 'theme', tag: 'garden', count: 2 },
      { type: 'tag', tag: 'seat', count: 1 },
    ],
  },
  {
    id: 'shoot_bao_cv',
    npcId: 'npc_bao',
    briefKey: 'studio.shoot.shoot_bao_cv.brief',
    criteria: [
      { type: 'tag', tag: 'light', count: 2 },
      { type: 'theme', tag: 'cozy', count: 3 },
    ],
  },
  {
    id: 'shoot_bao_mom',
    npcId: 'npc_bao',
    briefKey: 'studio.shoot.shoot_bao_mom.brief',
    rewardCoins: 80,
    criteria: [
      { type: 'cozy', points: 60 },
      { type: 'tag', tag: 'seat', count: 1 },
    ],
    conditions: [studioLevel(3)],
  },
  // ---- Bé Mít (and Sếp Mèo, the model) ----
  {
    id: 'shoot_mit_cat',
    npcId: 'npc_mit',
    model: 'npc_boss_cat',
    briefKey: 'studio.shoot.shoot_mit_cat.brief',
    isFirst: true,
    criteria: [
      { type: 'theme', tag: 'pet', count: 1 },
      { type: 'theme', tag: 'cute', count: 2 },
    ],
  },
  {
    id: 'shoot_mit_school',
    npcId: 'npc_mit',
    briefKey: 'studio.shoot.shoot_mit_school.brief',
    criteria: [
      { type: 'tag', tag: 'book', count: 1 },
      { type: 'tag', tag: 'seat', count: 1 },
      { type: 'theme', tag: 'cute', count: 1 },
    ],
  },
  {
    id: 'shoot_mit_cat_nap',
    npcId: 'npc_mit',
    model: 'npc_boss_cat',
    briefKey: 'studio.shoot.shoot_mit_cat_nap.brief',
    criteria: [
      { type: 'tag', tag: 'rug', count: 1 },
      { type: 'theme', tag: 'cozy', count: 2 },
      { type: 'theme', tag: 'pet', count: 1 },
    ],
  },
  {
    id: 'shoot_mit_birthday',
    npcId: 'npc_mit',
    briefKey: 'studio.shoot.shoot_mit_birthday.brief',
    criteria: [
      { type: 'item', itemId: 'dec_studio_balloons' },
      { type: 'theme', tag: 'sweet', count: 1 },
      { type: 'theme', tag: 'cute', count: 2 },
    ],
    conditions: [studioLevel(2)],
  },
  {
    id: 'shoot_mit_cat_king',
    npcId: 'npc_mit',
    model: 'npc_boss_cat',
    briefKey: 'studio.shoot.shoot_mit_cat_king.brief',
    rewardCoins: 80,
    criteria: [
      { type: 'tag', tag: 'seat', count: 1 },
      { type: 'tag', tag: 'light', count: 2 },
      { type: 'theme', tag: 'pet', count: 1 },
    ],
    conditions: [studioLevel(4)],
  },
  // ---- Cô Lan ----
  {
    id: 'shoot_lan_flowers',
    npcId: 'npc_lan',
    briefKey: 'studio.shoot.shoot_lan_flowers.brief',
    isFirst: true,
    criteria: [
      { type: 'tag', tag: 'plant', count: 2 },
      { type: 'theme', tag: 'sweet', count: 1 },
    ],
  },
  {
    id: 'shoot_lan_young',
    npcId: 'npc_lan',
    briefKey: 'studio.shoot.shoot_lan_young.brief',
    criteria: [
      { type: 'theme', tag: 'cute', count: 2 },
      { type: 'tag', tag: 'light', count: 2 },
    ],
  },
  {
    id: 'shoot_lan_bestie',
    npcId: 'npc_lan',
    briefKey: 'studio.shoot.shoot_lan_bestie.brief',
    criteria: [
      { type: 'item', itemId: 'dec_grandma_photo' },
      { type: 'theme', tag: 'cozy', count: 3 },
    ],
  },
  {
    id: 'shoot_lan_candy',
    npcId: 'npc_lan',
    briefKey: 'studio.shoot.shoot_lan_candy.brief',
    criteria: [
      { type: 'wallFloor', ids: ['wall_pink_stripe', 'wall_mint_dots'] },
      { type: 'theme', tag: 'cute', count: 1 },
      { type: 'tag', tag: 'plant', count: 1 },
    ],
    conditions: [studioLevel(2)],
  },
] as const satisfies readonly StudioShootDefInput[];

/**
 * Tiếng lành level rewards (studio-nano.md §5), one list per balance.studio.levels entry, paid once each with
 * key `studio:L<n>`. What a level opens (catalog, expansion, chapters) lives in the unl_studio_lv* unlocks.
 */
const STARS = { type: 'stars', amount: BALANCE.starsPer.studioLevel } as const;

export const STUDIO_LEVEL_REWARDS: readonly (readonly RewardInput[])[] = [
  [STARS, { type: 'coins', amount: 50 }],
  [STARS],
  [STARS],
  [STARS, { type: 'coins', amount: 150 }],
  [STARS, { type: 'title', titleId: 'ttl_studio_photographer' }],
];
